/**
 * Auto-add/remove complimentary gifts when qualifying routines are in cart.
 * Each routine has its own gift (Face → Body Moisturizer, Body → Face Moisturizer, …),
 * mapped per variant in window.GWP_CONFIG.giftByVariant (snippets/component-gwp-config.liquid).
 * One line per distinct gift product, quantity 1, however many routines point at it.
 * Promo lines are tagged with _promo_free_gift; price is zeroed at checkout by the discount function.
 */
(function () {
  const config = window.GWP_CONFIG || {};
  const GIFT_BY_VARIANT = config.giftByVariant || {};
  const PROMO_PROPERTY = '_promo_free_gift';

  let isSyncingGwp = false;

  function isGwpConfigured() {
    return Object.keys(GIFT_BY_VARIANT).length > 0;
  }

  function isPromoGiftLine(item) {
    return item.properties && item.properties[PROMO_PROPERTY] === 'true';
  }

  // A line qualifies only when the routine is bought as a subscription
  // (selling plan applied) — matches the Shopify "Buy X as a subscription
  // purchase, get Y free" automatic discount. One-time purchases do not qualify.
  function isSubscriptionItem(item) {
    return Boolean(item.selling_plan_allocation);
  }

  function getExpectedGiftVariantIds(items) {
    const giftIds = new Set();

    for (const item of items) {
      if (isPromoGiftLine(item) || !isSubscriptionItem(item)) continue;
      const giftId = GIFT_BY_VARIANT[item.variant_id];
      if (giftId) giftIds.add(giftId);
    }

    return giftIds;
  }

  async function fetchCart() {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  }

  async function addPromoGifts(giftVariantIds) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: giftVariantIds.map((id) => ({
          id,
          quantity: 1,
          properties: { [PROMO_PROPERTY]: 'true' },
        })),
      }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Failed to add promo gift');
    }
    return res.json();
  }

  async function changeLineQuantity(lineKey, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Failed to update cart');
    }
    return res.json();
  }

  async function refreshCartUI() {
    if (window.liquidAjaxCart && typeof window.liquidAjaxCart.update === 'function') {
      await window.liquidAjaxCart.update();
    }
  }

  async function syncRoutineGwp() {
    if (isSyncingGwp || !isGwpConfigured()) return;

    isSyncingGwp = true;
    let cartMutated = false;

    try {
      const cart = await fetchCart();
      const items = cart.items || [];
      const expectedGiftIds = getExpectedGiftVariantIds(items);
      const keptGiftIds = new Set();

      // Remove gifts no routine asks for any more (routine removed, switched to
      // one-time, or its gift changed) and duplicates; cap the kept line at 1.
      for (const line of items.filter(isPromoGiftLine)) {
        if (!expectedGiftIds.has(line.variant_id) || keptGiftIds.has(line.variant_id)) {
          await changeLineQuantity(line.key, 0);
          cartMutated = true;
          continue;
        }

        keptGiftIds.add(line.variant_id);
        if (line.quantity > 1) {
          await changeLineQuantity(line.key, 1);
          cartMutated = true;
        }
      }

      const missingGiftIds = [...expectedGiftIds].filter((id) => !keptGiftIds.has(id));
      if (missingGiftIds.length > 0) {
        await addPromoGifts(missingGiftIds);
        cartMutated = true;
      }

      if (cartMutated) {
        await refreshCartUI();
      }
    } catch (error) {
      console.error('[routine-gwp] sync error:', error);
    } finally {
      isSyncingGwp = false;
    }
  }

  function onCartRequestEnd(event) {
    if (isSyncingGwp || !isGwpConfigured()) return;

    const { requestState } = event.detail || {};
    if (requestState?.responseData?.ok === false) return;
    // Avoid feedback loops: liquidAjaxCart.update() fetches sections only
    // (no cart state change), so we don't need to re-sync GWP on those events.
    if (requestState?.requestType === 'update') return;

    syncRoutineGwp();
  }

  function init() {
    if (!isGwpConfigured()) return;

    document.addEventListener('liquid-ajax-cart:request-end', onCartRequestEnd);
    syncRoutineGwp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
